'use client'

import { useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import HumanCheck from '@/components/security/HumanCheck'
import PhonePrefixPicker from '@/components/ui/phone-prefix-picker'
import AttributionHiddenFields from '@/components/tracking/attribution-hidden-fields'
import { isDigitsOnlyPhone, isValidEmailFormat } from '@/lib/lead-validation'
import { completeLeadSubmission, getLeadTrackingPayload } from '@/lib/lead-tracking-client'
import {
  LANDING_SERVICE_OPTIONS,
  MOVE_TIMELINE_OPTIONS,
  type LandingServiceKey,
  type MoveTimelineKey,
} from '@/lib/landing-pages'

type Locale = 'es' | 'en'
type FieldName = 'name' | 'email' | 'phone' | 'country' | 'privacy'
type FieldErrors = Partial<Record<FieldName, string>>

const COPY = {
  es: {
    name: 'Nombre',
    email: 'Email',
    phone: 'Teléfono / WhatsApp',
    country: 'País de residencia',
    countryPlaceholder: 'Ej. Estados Unidos',
    service: 'Servicio',
    moveTimeline: '¿Cuándo quieres mudarte?',
    privacyPrefix: 'He leído y acepto la',
    privacyLink: 'política de privacidad',
    submit: 'Revisar mi caso',
    sending: 'Enviando…',
    required: 'Campo obligatorio.',
    emailInvalid: 'El email no es válido.',
    phoneInvalid: 'Introduce solo números.',
    privacyRequired: 'Debes aceptar la política de privacidad.',
    captchaRequired: 'Completa la verificación anti-bot antes de enviar.',
    submitFailed: 'No se pudo enviar. Inténtalo de nuevo o escríbenos por WhatsApp.',
    origin: 'Landing Google Ads',
    detailsCountry: 'País de residencia',
    detailsMove: 'Cuándo quiere mudarse',
  },
  en: {
    name: 'Name',
    email: 'Email',
    phone: 'Phone / WhatsApp',
    country: 'Country of residence',
    countryPlaceholder: 'e.g. United States',
    service: 'Service',
    moveTimeline: 'When do you want to move?',
    privacyPrefix: 'I have read and accept the',
    privacyLink: 'privacy policy',
    submit: 'Get my case reviewed',
    sending: 'Sending…',
    required: 'Required field.',
    emailInvalid: 'The email is not valid.',
    phoneInvalid: 'Digits only, please.',
    privacyRequired: 'You must accept the privacy policy.',
    captchaRequired: 'Complete the anti-bot verification before submitting.',
    submitFailed: 'We could not send your request. Try again or message us on WhatsApp.',
    origin: 'Google Ads landing',
    detailsCountry: 'Country of residence',
    detailsMove: 'Moving timeline',
  },
} as const

export default function LandingLeadForm({
  locale,
  defaultService,
  formId,
}: {
  locale: Locale
  defaultService: LandingServiceKey
  formId: string
}) {
  const copy = COPY[locale]
  const router = useRouter()
  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '' })
  const [phonePrefix, setPhonePrefix] = useState('+34')
  const [service, setService] = useState<LandingServiceKey>(defaultService)
  const [moveTimeline, setMoveTimeline] = useState<MoveTimelineKey>('lt3')
  const [privacy, setPrivacy] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaResetCounter, setCaptchaResetCounter] = useState(0)
  const [captchaRequired, setCaptchaRequired] = useState(process.env.NEXT_PUBLIC_ANTI_BOT_ENABLED === 'true')
  // Turnstile (~400 KB) only loads once the visitor starts filling the form: keeps the landing fast
  const [engaged, setEngaged] = useState(false)

  const selectedService = LANDING_SERVICE_OPTIONS[service]
  const selectedTimeline = MOVE_TIMELINE_OPTIONS[moveTimeline]

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name as FieldName]) setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  const validate = (): FieldErrors => {
    const next: FieldErrors = {}
    if (!form.name.trim()) next.name = copy.required
    if (!form.email.trim()) next.email = copy.required
    else if (!isValidEmailFormat(form.email)) next.email = copy.emailInvalid
    if (!form.phone.trim()) next.phone = copy.required
    else if (!isDigitsOnlyPhone(form.phone.replace(/[\s-]/g, ''))) next.phone = copy.phoneInvalid
    if (!form.country.trim()) next.country = copy.required
    if (!privacy) next.privacy = copy.privacyRequired
    return next
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (loading) return
    const nextErrors = validate()
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    if (captchaRequired && !captchaToken) {
      setSubmitError(copy.captchaRequired)
      return
    }

    setLoading(true)
    setSubmitError('')
    const phone = `${phonePrefix}${form.phone.replace(/[\s-]/g, '')}`
    const country = form.country.trim()
    const timelineLabel = selectedTimeline[locale]

    try {
      const response = await fetch('/api/mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone,
          inquiry: selectedService.inquiry,
          serviceId: selectedService.serviceId,
          requestDetails: `${copy.detailsCountry}: ${country}. ${copy.detailsMove}: ${timelineLabel}.`,
          budget: 'N/A',
          deadline: timelineLabel,
          origin: copy.origin,
          sourceFlow: 'landing_ads',
          captchaToken,
          leadLanguage: locale,
          country,
          moveTimeline: timelineLabel,
          ...getLeadTrackingPayload(selectedService.servicio),
        }),
      })

      if (!response.ok) throw new Error('submit_failed')
      completeLeadSubmission((url) => router.push(url), { servicio: selectedService.servicio, locale })
    } catch {
      setSubmitError(copy.submitFailed)
      setLoading(false)
      setCaptchaToken('')
      setCaptchaResetCounter((value) => value + 1)
    }
  }

  const inputClass = (error?: string) =>
    `w-full rounded-lg border bg-gray-950 px-3 py-2.5 text-sm text-gray-100 placeholder-gray-500 outline-none focus:ring-1 ${
      error ? 'border-red-500 focus:ring-red-400' : 'border-gray-700 focus:border-amber-400 focus:ring-amber-400'
    }`
  const labelClass = 'mb-1 block text-xs font-medium text-gray-300'
  const fieldId = (name: string) => `${formId}-${name}`

  return (
    <form id={formId} onSubmit={onSubmit} onFocus={() => setEngaged(true)} noValidate className="space-y-3">
      <AttributionHiddenFields servicio={selectedService.servicio} />

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={fieldId('name')} className={labelClass}>
            {copy.name} <span className="text-red-400">*</span>
          </label>
          <input id={fieldId('name')} name="name" autoComplete="name" value={form.name} onChange={onChange} className={inputClass(errors.name)} />
          {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={fieldId('email')} className={labelClass}>
            {copy.email} <span className="text-red-400">*</span>
          </label>
          <input id={fieldId('email')} name="email" type="email" autoComplete="email" value={form.email} onChange={onChange} className={inputClass(errors.email)} />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
        </div>
      </div>

      <div>
        <label htmlFor={fieldId('phone')} className={labelClass}>
          {copy.phone} <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-2">
          <PhonePrefixPicker value={phonePrefix} onChange={setPhonePrefix} variant="dark" />
          <input
            id={fieldId('phone')}
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={form.phone}
            onChange={onChange}
            className={`${inputClass(errors.phone)} min-w-0 flex-1`}
          />
        </div>
        {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone}</p>}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={fieldId('country')} className={labelClass}>
            {copy.country} <span className="text-red-400">*</span>
          </label>
          <input
            id={fieldId('country')}
            name="country"
            autoComplete="country-name"
            placeholder={copy.countryPlaceholder}
            value={form.country}
            onChange={onChange}
            className={inputClass(errors.country)}
          />
          {errors.country && <p className="mt-1 text-xs text-red-400">{errors.country}</p>}
        </div>
        <div>
          <label htmlFor={fieldId('service')} className={labelClass}>
            {copy.service}
          </label>
          <select
            id={fieldId('service')}
            name="service"
            value={service}
            onChange={(event) => setService(event.target.value as LandingServiceKey)}
            className={inputClass()}
          >
            {(Object.keys(LANDING_SERVICE_OPTIONS) as LandingServiceKey[]).map((key) => (
              <option key={key} value={key}>
                {LANDING_SERVICE_OPTIONS[key][locale]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={fieldId('move')} className={labelClass}>
          {copy.moveTimeline}
        </label>
        <select
          id={fieldId('move')}
          name="moveTimeline"
          value={moveTimeline}
          onChange={(event) => setMoveTimeline(event.target.value as MoveTimelineKey)}
          className={inputClass()}
        >
          {(Object.keys(MOVE_TIMELINE_OPTIONS) as MoveTimelineKey[]).map((key) => (
            <option key={key} value={key}>
              {MOVE_TIMELINE_OPTIONS[key][locale]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="flex items-start gap-2 text-xs text-gray-300">
          <input
            type="checkbox"
            name="privacy"
            checked={privacy}
            onChange={(event) => {
              setPrivacy(event.target.checked)
              if (errors.privacy) setErrors((prev) => ({ ...prev, privacy: undefined }))
            }}
            className="mt-0.5 rounded border-gray-600 bg-gray-950 text-amber-400"
          />
          <span>
            {copy.privacyPrefix}{' '}
            <a href={locale === 'en' ? '/en/privacidad' : '/privacidad'} target="_blank" className="underline hover:text-white">
              {copy.privacyLink}
            </a>{' '}
            <span className="text-red-400">*</span>
          </span>
        </label>
        {errors.privacy && <p className="mt-1 text-xs text-red-400">{errors.privacy}</p>}
      </div>

      {engaged && (
        <HumanCheck
          action="landing_lead"
          resetCounter={captchaResetCounter}
          onTokenChange={setCaptchaToken}
          onRequirementChange={setCaptchaRequired}
        />
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-amber-400 py-3.5 text-base font-black text-gray-900 transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? copy.sending : copy.submit}
      </button>

      {submitError && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {submitError}
        </p>
      )}
    </form>
  )
}
